/**
 * DestructiveConfirmFlow.jsx — Phase 84.23.9
 *
 * Shared four-layer type-to-confirm primitive for any destructive user
 * action. Currently powers:
 *
 *   • Settings → Profile → Danger Zone → "Delete all data"
 *     (see components/settings/DangerZone.jsx)
 *   • Settings → Help → "Explore demo" when the user has real data
 *     (see components/settings/DemoDataSettingsCard.jsx)
 *
 * Layers:
 *   1. Warn                — "Are you sure?"
 *   2. Reason picker (opt) — six preset reasons + optional / required note
 *   3. Bold red disclaimer + type-"delete" to confirm
 *   4. Busy state on layer 3's button while onConfirm runs
 *
 * The consumer owns the destructive network call via `onConfirm`. The
 * primitive owns the layer state, the type-to-confirm gate, and the
 * "close on backdrop / ESC / Cancel only when it's safe" logic.
 *
 * Uses plain <Button> for forward transitions to defeat the Radix
 * AlertDialogAction auto-close race that was resetting layer state
 * before the next Dialog mounted (RCA: /app/memory/PRD.md · Phase 84.23.5).
 *
 * A11y: `AlertDialogDescription` renders as a plain string so Radix's
 * automatic `aria-describedby` wiring works. Any visual embellishment
 * (red disclaimer box, radiogroup, textarea, input) lives as a sibling
 * of the description, not inside it. This silences the "Missing
 * Description or aria-describedby={undefined}" Radix warning.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

// Kept in sync with the backend `_VALID_PURGE_REASONS` enum and the
// SQL CHECK constraint in schema_phase84_23_6_data_purge_events.sql.
export const REASON_OPTIONS = [
  { value: 'start_fresh',     label: 'I want to start fresh with my own data' },
  { value: 'wrong_import',    label: 'I imported the wrong statements or made too many mistakes' },
  { value: 'app_not_working', label: "The app isn't working the way I expected" },
  { value: 'privacy',         label: "Privacy — I don't want this data stored" },
  { value: 'no_longer_using', label: "I'm no longer using 2120" },
  { value: 'other',           label: 'Other (please tell us)' },
];

/**
 * @param {object} props
 * @param {boolean} props.open                   Controlled: whether the flow is open.
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {string}  [props.testIdPrefix='destructive']  For unique data-testids across callers.
 * @param {string}  props.warnTitle              L1 title.
 * @param {string}  props.warnBody               L1 description (plain text).
 * @param {string}  [props.reasonPrompt='Why are you doing this?']  L2 title.
 * @param {string}  [props.reasonBody]           L2 description (plain text).
 * @param {string}  props.disclaimer             L3 red-box copy (plain text).
 * @param {string}  [props.disclaimerHelper]     L3 helper below the red box (plain text).
 * @param {string}  props.confirmVerb            L3 button text ("Delete all my data").
 * @param {string}  [props.busyVerb='Working…']  L3 spinner text.
 * @param {(payload: {reason: string, reason_note: string|null}) => Promise<void>} props.onConfirm
 */
export default function DestructiveConfirmFlow({
  open,
  onOpenChange,
  testIdPrefix = 'destructive',
  warnTitle,
  warnBody,
  reasonPrompt = 'Help us understand why',
  reasonBody = 'Your answer is private — only the 2120 team can see it. Please pick one before continuing.',
  disclaimer,
  disclaimerHelper,
  confirmVerb,
  busyVerb = 'Working…',
  onConfirm,
}) {
  const [layer, setLayer] = useState(1);       // 1 warn, 2 reason, 3 confirm
  const [reason, setReason] = useState(null);
  const [reasonNote, setReasonNote] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset transient state each time the flow reopens so the previous
  // session's reason / typed value doesn't leak into a new attempt.
  useEffect(() => {
    if (open) {
      setLayer(1);
      setReason(null);
      setReasonNote('');
      setTyped('');
      setBusy(false);
    }
  }, [open]);

  const close = () => {
    if (busy) return;
    onOpenChange?.(false);
  };

  const canAdvanceFromReason =
    !!reason && (reason !== 'other' || reasonNote.trim().length >= 3);

  const runConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm?.({
        reason: reason || 'other',
        reason_note: reasonNote.trim() || null,
      });
    } finally {
      // Always close so an error-throwing consumer can't leave the flow
      // hanging. Consumers that want to keep the dialog open on error
      // should return a resolved promise and handle the error inside
      // onConfirm rather than throwing.
      setBusy(false);
      onOpenChange?.(false);
    }
  };

  const testId = (name) => `${testIdPrefix}-${name}`;

  return (
    <>
      {/* Layer 1 — warn */}
      <AlertDialog
        open={open && layer === 1}
        onOpenChange={(o) => { if (!o && layer === 1) close(); }}
      >
        <AlertDialogContent data-testid={testId('warn-layer1')}>
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-primary">
              <AlertTriangle className="h-5 w-5" />
              <AlertDialogTitle>{warnTitle}</AlertDialogTitle>
            </div>
            <AlertDialogDescription>{warnBody}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button
              onClick={() => setLayer(2)}
              disabled={busy}
              data-testid={testId('warn-layer1-continue')}
            >
              Continue
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Layer 2 — reason (must pick one; 'other' requires a note) */}
      <AlertDialog
        open={open && layer === 2}
        onOpenChange={(o) => { if (!o && layer === 2) close(); }}
      >
        <AlertDialogContent data-testid={testId('warn-layer2')}>
          <AlertDialogHeader>
            <AlertDialogTitle>{reasonPrompt}</AlertDialogTitle>
            <AlertDialogDescription>{reasonBody}</AlertDialogDescription>
          </AlertDialogHeader>
          <div
            role="radiogroup"
            aria-label="Reason"
            className="space-y-2 mt-1"
            data-testid={testId('reason-group')}
          >
            {REASON_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                htmlFor={`${testIdPrefix}-reason-${opt.value}`}
                className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                  reason === opt.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/30'
                }`}
              >
                <input
                  id={`${testIdPrefix}-reason-${opt.value}`}
                  type="radio"
                  name={`${testIdPrefix}-reason`}
                  value={opt.value}
                  checked={reason === opt.value}
                  onChange={() => setReason(opt.value)}
                  className="mt-1 accent-primary"
                  data-testid={testId(`reason-${opt.value}`)}
                />
                <span className="text-sm text-charcoal leading-snug">{opt.label}</span>
              </label>
            ))}
          </div>
          {reason === 'other' && (
            <div className="mt-3">
              <label htmlFor={`${testIdPrefix}-reason-other-note`} className="sr-only">Tell us more</label>
              <Textarea
                id={`${testIdPrefix}-reason-other-note`}
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                placeholder="What's driving this? (at least 3 characters)"
                rows={3}
                maxLength={1000}
                data-testid={testId('reason-other-note')}
              />
              <p className="text-xs text-muted-foreground mt-1">{reasonNote.trim().length}/1000</p>
            </div>
          )}
          {reason && reason !== 'other' && (
            <div className="mt-3">
              <label htmlFor={`${testIdPrefix}-reason-note`} className="text-xs text-muted-foreground mb-1 block">
                Anything else you&rsquo;d like to add? (optional)
              </label>
              <Textarea
                id={`${testIdPrefix}-reason-note`}
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                placeholder="Optional — helps us fix things"
                rows={2}
                maxLength={1000}
                data-testid={testId('reason-optional-note')}
              />
            </div>
          )}
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button
              onClick={() => setLayer(3)}
              disabled={busy || !canAdvanceFromReason}
              className="disabled:opacity-40"
              data-testid={testId('warn-layer2-continue')}
            >
              Continue
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Layer 3 — bold red disclaimer + type-to-confirm */}
      <AlertDialog
        open={open && layer === 3}
        onOpenChange={(o) => { if (!o && layer === 3) close(); }}
      >
        <AlertDialogContent data-testid={testId('warn-layer3')}>
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              <AlertDialogTitle>Last step — type &ldquo;delete&rdquo; to confirm</AlertDialogTitle>
            </div>
            {/* Keep AlertDialogDescription a plain string so Radix
                links aria-describedby automatically. Visual disclaimer
                box lives as a sibling below. */}
            <AlertDialogDescription>
              {disclaimerHelper || 'To confirm, type the word "delete" below.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div
            className="rounded-lg border-2 border-destructive/60 bg-destructive/10 p-3 text-destructive font-semibold text-sm leading-snug"
            data-testid={testId('warn-layer3-disclaimer')}
          >
            ⚠ {disclaimer}
          </div>
          <div className="mt-2">
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Type delete to confirm"
              autoFocus
              data-testid={testId('warn-layer3-input')}
            />
          </div>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button
              onClick={runConfirm}
              disabled={busy || typed.trim().toLowerCase() !== 'delete'}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-40"
              data-testid={testId('warn-layer3-confirm')}
            >
              {busy ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {busyVerb}</>
              ) : (
                confirmVerb
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
